import { useEffect, useState } from 'react';
import { AlertCircle, Building2, Loader2, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api, ApiError } from '@/lib/api';
// Одна карта меток на весь фронт (ADR-081 §4).
import { roleLabel } from '@/pages/team/teamLogic';

/**
 * /auth/select-tenant — экран выбора кабинета (AK-660).
 *
 * До этого экрана человек с двумя членствами был заперт: вход не выбирал
 * кабинет за него (и правильно — выбирать некому), а места, где выбрать, в
 * системе не существовало. Ссылки на `/auth/select-tenant` вели в 404 с тех
 * пор, как вместе со старым фронтом уехала легаси-страница.
 *
 * Standalone (БЕЗ AppShell): AppShell сам уводит сюда, пока кабинет не выбран,
 * и вложенность закончилась бы циклом.
 *
 * Тот же экран обслуживает `/auth/select-context` — развилку для
 * администратора платформы, у которого есть и кабинеты, и админка. Разница
 * ровно в одной дополнительной карточке, поэтому отдельного экрана (и второго
 * места, где этот выбор может протухнуть) не заводим.
 *
 * После выбора — ПОЛНАЯ перезагрузка, а не router.navigate: активный кабинет
 * живёт в токене, и всё уже загруженное на клиенте относится к прошлому
 * (или к отсутствующему) кабинету.
 */

interface Membership {
  tenant: { id: string; name: string };
  role: string;
}

interface Me {
  user: { platform_role?: string | null };
  active_tenant?: { id: string } | null;
  memberships?: Membership[];
}


export function SelectTenantPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<Me>('/api/v1/auth/me');
        if (!cancelled) setMe(data);
      } catch (err) {
        // 401 api.ts уводит на /login сам; сюда доходит остальное.
        if (!cancelled) {
          setError(err instanceof ApiError ? err.detail : 'Не удалось получить список кабинетов');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = async (tenantId: string) => {
    setError(null);
    setPendingId(tenantId);
    try {
      await api.post('/api/v1/auth/select-tenant', { tenant_id: tenantId });
      window.location.href = '/dashboard';
    } catch (err) {
      setPendingId(null);
      setError(err instanceof ApiError ? err.detail : 'Не удалось открыть кабинет');
    }
  };

  const memberships = me?.memberships ?? [];
  const isPlatformAdmin = !!me?.user?.platform_role;

  return (
    <div
      className="min-h-dvh flex items-center justify-center p-6"
      style={{
        background: 'linear-gradient(135deg, #0B1F1A 0%, #102A23 60%, #1F6F4A 100%)',
      }}
    >
      <Card className="max-w-lg w-full">
        <CardContent className="pt-6 space-y-4">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Выберите кабинет</h1>
            <p className="text-sm text-muted-foreground mt-1">
              У вас есть доступ к нескольким компаниям. Кабинет можно сменить в
              любой момент — в шапке, рядом с названием компании.
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : (
            <>
              {memberships.length === 0 && !isPlatformAdmin && (
                <Alert>
                  <Building2 className="h-4 w-4" />
                  <AlertTitle>Кабинетов пока нет</AlertTitle>
                  <AlertDescription>
                    Вас ещё не пригласили ни в одну компанию. Попросите
                    администратора выслать приглашение.
                  </AlertDescription>
                </Alert>
              )}

              <ul className="space-y-2">
                {memberships.map((m) => {
                  const busy = pendingId === m.tenant.id;
                  return (
                    <li key={m.tenant.id}>
                      <button
                        type="button"
                        disabled={pendingId !== null}
                        onClick={() => void choose(m.tenant.id)}
                        className="w-full flex items-center gap-3 rounded-md border border-border bg-card px-3 py-3 text-left transition-colors hover:bg-secondary/60 disabled:opacity-60"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/5 border border-primary/15 text-primary">
                          <Building2 className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">
                            {m.tenant.name}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {roleLabel(m.role)}
                          </span>
                        </span>
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : (
                          <span className="text-sm text-primary shrink-0">Войти</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {isPlatformAdmin && (
                <>
                  <p className="text-xs text-muted-foreground">
                    Или откройте служебную часть платформы:
                  </p>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    disabled={pendingId !== null}
                    onClick={() => {
                      window.location.href = '/platform-admin';
                    }}
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Администрирование платформы
                  </Button>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
